import { useEffect, useState } from "react";

export const MOBILE_CARD_WIDTH = 235;
export const DESKTOP_CARD_WIDTH = 285;
// Matches the previous `window.innerWidth < 600` cutoff (MUI sm breakpoint)
const MOBILE_MEDIA_QUERY = "(max-width: 599.95px)";

/**
 * Card width per viewport: 235px below the 600px breakpoint, 285px above.
 * Shared by Carousel (auto-scroll) and Cards (native scroll) so the two
 * variants can't drift apart.
 *
 * Uses a matchMedia "change" listener, which fires only when the breakpoint
 * is actually crossed — unlike the previous window resize listener that fired
 * (debounced) on every drag of the window edge.
 */
const useCardWidth = (): number => {
  const [cardWidth, setCardWidth] = useState(DESKTOP_CARD_WIDTH);

  useEffect(() => {
    // jsdom (unit tests) has no matchMedia — fall back to a one-time check
    if (typeof window.matchMedia !== "function") {
      setCardWidth(window.innerWidth < 600 ? MOBILE_CARD_WIDTH : DESKTOP_CARD_WIDTH);
      return;
    }
    const mql = window.matchMedia(MOBILE_MEDIA_QUERY);
    const update = () => setCardWidth(mql.matches ? MOBILE_CARD_WIDTH : DESKTOP_CARD_WIDTH);
    update();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", update);
      return () => mql.removeEventListener("change", update);
    }
    // Safari < 14 only supports the deprecated addListener API
    if (typeof mql.addListener === "function") {
      mql.addListener(update);
      return () => mql.removeListener(update);
    }
    // Neither API available: the one-time update() above still set the
    // correct width for the current viewport
    return undefined;
  }, []);

  return cardWidth;
};

export default useCardWidth;
